import { useMemo, useState } from 'react';
import { ClipboardList } from 'lucide-react';
import clsx from 'clsx';
import { useTranslation } from '@/i18n';
import StyledButton from '@/components/styled/StyledButton';

export type ExcelPasteShape =
  | 'pension'
  | 'pensionTariffs'
  | 'supplement'
  | 'supplementTariffs'
  | 'room'
  | 'roomCategory';

export interface ExcelPasteRecapRow {
  key: string;
  label: string;
  current: string;
  pasted: string;
}

export interface ExcelPasteAnalysis {
  /** Blocking: when not empty, nothing is applied. */
  errors: string[];
  warnings: string[];
  /** What the paste does besides the recap fields (checkboxes it changes). */
  notes?: string[];
  recap: ExcelPasteRecapRow[];
}

interface ExcelPastePanelProps {
  shape: ExcelPasteShape;
  analyze: (text: string) => ExcelPasteAnalysis;
  apply: (text: string) => void | Promise<void>;
  /** When set, pasting is not offered and the panel explains why. */
  disabledReason?: string;
  /** Panel title, "Coller depuis Excel" by default. */
  title?: string;
}

type LegendLine = { lines: string; label: string };

/**
 * Expert mode paste zone shown next to a tariff card: paste the grid block,
 * check the errors, warnings and recap, then fill the form with "Appliquer".
 * Nothing is saved here, saving stays the form's own button.
 */
const ExcelPastePanel = ({
  shape,
  analyze,
  apply,
  disabledReason,
  title,
}: ExcelPastePanelProps) => {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [isApplying, setIsApplying] = useState(false);

  const analysis = useMemo(
    () => (text.trim() === '' ? null : analyze(text)),
    [analyze, text],
  );
  const canApply =
    !disabledReason &&
    !isApplying &&
    analysis !== null &&
    analysis.errors.length === 0;

  const legend = t(`hotels.pasteFromExcel.legend.${shape}`, {
    returnObjects: true,
  }) as unknown as LegendLine[];

  const onApply = async () => {
    if (!canApply) return;
    setIsApplying(true);
    try {
      await apply(text);
      setText('');
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <aside className="flex h-fit flex-col gap-4 rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex items-center gap-2">
        <ClipboardList className="size-5 stroke-brand-600" />
        <h3 className="text-base font-semibold text-gray-950">
          {title ?? t('hotels.pasteFromExcel.button')}
        </h3>
      </div>
      {disabledReason ? (
        <p className="text-sm leading-6 text-gray-700">{disabledReason}</p>
      ) : (
        <>
          <p className="text-sm leading-6 text-gray-700">
            {t(`hotels.pasteFromExcel.instructions.${shape}`)}
          </p>
          <textarea
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder={t('hotels.pasteFromExcel.placeholder')}
            className="min-h-28 w-full rounded-lg border-2 border-dashed border-brand-300 bg-brand-50 p-3 font-mono text-xs text-gray-900 placeholder:text-brand-700 focus-visible:outline-hidden focus-visible:border-brand-600"
          />
          <div className="flex gap-3">
            <StyledButton
              type="button"
              variant="outlined"
              title={t('common.cancel')}
              className="w-full border-gray-300 text-gray-800"
              disabled={text === '' || isApplying}
              onClick={() => setText('')}
            />
            <StyledButton
              type="button"
              title={t('hotels.pasteFromExcel.apply')}
              className="w-full"
              disabled={!canApply}
              onClick={onApply}
            />
          </div>
          {analysis && analysis.errors.length > 0 && (
            <IssueList
              title={t('hotels.pasteFromExcel.errorsTitle')}
              issues={analysis.errors}
              className="border-error-300 bg-white text-error-700"
            />
          )}
          {analysis && analysis.errors.length === 0 && (
            <>
              {analysis.warnings.length > 0 && (
                <IssueList
                  title={t('hotels.pasteFromExcel.warningsTitle')}
                  issues={analysis.warnings}
                  className="border-warning-200 bg-warning-50 text-warning-700"
                />
              )}
              {analysis.notes && analysis.notes.length > 0 && (
                <IssueList
                  title={t('hotels.pasteFromExcel.notesTitle')}
                  issues={analysis.notes}
                  className="border-brand-200 bg-brand-50 text-brand-700"
                />
              )}
              <RecapTable rows={analysis.recap} />
            </>
          )}
        </>
      )}
      <div className="flex flex-col gap-1.5 border-t border-gray-100 pt-3">
        <p className="text-xs font-semibold text-gray-900">
          {t(`hotels.pasteFromExcel.shapes.${shape}`)}
        </p>
        <table className="font-mono text-xs text-gray-700">
          <tbody>
            {Array.isArray(legend) &&
              legend.map(({ lines, label }) => (
                <tr key={lines} className="align-top">
                  <td className="w-12 pr-2 whitespace-nowrap">{lines}</td>
                  <td>{label}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </aside>
  );
};

const IssueList = ({
  title,
  issues,
  className,
}: {
  title: string;
  issues: string[];
  className: string;
}) => (
  <div className={clsx('flex flex-col gap-1 rounded-lg border p-3', className)}>
    <p className="text-sm font-semibold">{title}</p>
    <ul className="list-disc pl-5 text-xs leading-5">
      {issues.map((issue, index) => (
        <li key={`${index}-${issue}`}>{issue}</li>
      ))}
    </ul>
  </div>
);

const RecapTable = ({ rows }: { rows: ExcelPasteRecapRow[] }) => {
  const { t } = useTranslation();
  const changedCount = rows.filter(
    ({ current, pasted }) => current !== pasted,
  ).length;

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-semibold text-gray-900">
        {t('hotels.pasteFromExcel.recapTitle', { count: changedCount })}
      </p>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-gray-200 text-left text-gray-700">
            <th className="p-1 font-medium">
              {t('hotels.pasteFromExcel.recapField')}
            </th>
            <th className="p-1 font-medium">
              {t('hotels.pasteFromExcel.recapCurrent')}
            </th>
            <th className="p-1 font-medium">
              {t('hotels.pasteFromExcel.recapPasted')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ key, label, current, pasted }) => {
            const isChanged = current !== pasted;
            return (
              <tr
                key={key}
                className={clsx('border-b border-gray-100', {
                  'bg-warning-50 font-medium text-gray-950': isChanged,
                  'text-gray-500': !isChanged,
                })}
              >
                <td className="p-1">{label}</td>
                <td className="p-1 whitespace-nowrap">{current || '—'}</td>
                <td className="p-1 whitespace-nowrap">{pasted || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="text-xs text-gray-600">
        {t('hotels.pasteFromExcel.recapNote')}
      </p>
    </div>
  );
};

export default ExcelPastePanel;
